import type {
  OpenSqlFileRequest,
  OpenSqlFileResult,
  QueryFileExternalChangeEvent,
  QueryFileExternalDeleteEvent,
  SaveSqlFileRequest,
  SaveSqlFileResult,
  WatchSqlFileRequest,
} from '../../../../types/queryFiles';

export async function openSqlFile(request: OpenSqlFileRequest = {}): Promise<OpenSqlFileResult> {
  if (!window.electronAPI?.queryFiles?.openSqlFile) {
    return { success: false, error: 'Open file API is unavailable' };
  }

  // E2E tests can't interact with native open dialogs. In test mode, allow a
  // localStorage override so UI flows can open deterministically without dialogs.
  const testOverridePath =
    !request.filePath &&
    window.electronAPI?.testMode?.isTestMode?.() &&
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('sql-studio-test-open-sql-file')?.trim() || undefined
      : undefined;

  const result = await window.electronAPI.queryFiles.openSqlFile({
    ...request,
    ...(testOverridePath ? { filePath: testOverridePath } : {}),
  });
  if (!result.success && result.reason === 'too-large') {
    // Every open path (toolbar, palette, tab action) funnels through here, so
    // the refusal is counted once. No path or size: both are user data.
    window.electronAPI?.analytics?.track('feature:open_sql_file_blocked_too_large', {}).catch(() => {});
  }
  return result;
}

export async function saveSqlFile(request: SaveSqlFileRequest): Promise<SaveSqlFileResult> {
  if (!window.electronAPI?.queryFiles?.saveSqlFile) {
    return { success: false, error: 'Save file API is unavailable' };
  }

  return window.electronAPI.queryFiles.saveSqlFile(request);
}

export async function watchSqlFile(request: WatchSqlFileRequest): Promise<void> {
  await window.electronAPI?.queryFiles?.watchSqlFile?.(request);
}

export async function unwatchSqlFile(request: WatchSqlFileRequest): Promise<void> {
  await window.electronAPI?.queryFiles?.unwatchSqlFile?.(request);
}

export function subscribeToQueryFileExternalChange(
  callback: (event: QueryFileExternalChangeEvent) => void,
): () => void {
  return window.electronAPI?.queryFiles?.onExternalChange?.(callback) ?? (() => {});
}

export function subscribeToQueryFileExternalDelete(
  callback: (event: QueryFileExternalDeleteEvent) => void,
): () => void {
  return window.electronAPI?.queryFiles?.onExternalDelete?.(callback) ?? (() => {});
}
